import { Checkbox, Col, Row, ScreenReaderOnly } from '@op/opds.opux';
import { useTranslation } from 'react-i18next';
import styles from './CoverageSelector.module.scss';
import CoveragePromoElement from '../CoveragePromoElement/CoveragePromoElement';
import { type Premium } from 'dto/CoveragePremium';
import { MarketableProductName, type Coverage } from 'dto/marketableProduct';
import { useAppStore } from 'state/store';
import { getInitialState } from 'utils';
import { formatCentsToEurosWithSeparators } from 'utils/currency-utils';

interface Props {
  coverage: Coverage;
  premium: Premium;
  discountComponent?: React.ReactNode;
  displayDetails: boolean;
}

const CoverageHeading = ({
  coverage,
  premium,
  discountComponent,
  displayDetails,
}: Props) => {
  const { t } = useTranslation(['individualLife']);
  const selectedCoverages = useAppStore.use.selectedCoverages();
  const toggleSelectedCoverages = useAppStore.use.toggleSelectedCoverages();

  const coverageProduct = coverage.coverageProduct;

  const coverageName = t(`coverageProduct.${coverageProduct}.name`);
  const marketableProduct = getInitialState().marketableProduct?.name;
  return (
    <div>
      <ScreenReaderOnly tagName='h3'>{coverageName}</ScreenReaderOnly>
      <Row
        data-testid={`coverageProduct-${coverageProduct}-tile-heading-row`}
        className={styles.tileHeadingRow}>
        <Col xs={12} sm={9} className={styles.noPadding}>
          <Row className={styles.coverageCheckboxRow}>
            {coverage.mandatory ? (
              <div>
                {marketableProduct !==
                  MarketableProductName.INDIVIDUAL_LIFE && (
                  <p
                    className={styles.coverageIncludesText}
                    data-testid={`coverageProduct-${coverageProduct}-includes-text`}>
                    {t('coverageProduct.coverageIncludes')}
                  </p>
                )}
                <div
                  className={styles.mandatoryCoverageLabel}
                  data-testid={`coverageProduct-${coverageProduct}-mandatory-heading`}>
                  {coverageName}
                </div>
              </div>
            ) : (
              <Checkbox
                data-testid={`coverageProduct-${coverageProduct}-select-checkbox`}
                checked={selectedCoverages.includes(coverageProduct)}
                aria-label={t(
                  `coverageProduct.coverageSelectCheckboxScreenReaderMessage`,
                  { insuranceName: coverageName }
                )}
                onChange={() => {
                  toggleSelectedCoverages(coverageProduct);
                }}
                size='hero'>
                <div className={styles.checkboxLabel}>
                  {t(`coverageProduct.${coverageProduct}.name`)}
                  {coverage.promoElement && (
                    <>
                      {' '}
                      <CoveragePromoElement
                        promoElement={coverage.promoElement}
                      />
                    </>
                  )}
                </div>
              </Checkbox>
            )}
          </Row>
          <p className={styles.keySellingPointText}>
            {t(`coverageProduct.${coverageProduct}.keySellingPointText`)}
          </p>
        </Col>
        {displayDetails && (
          <Col sm={3} className={styles.noPadding}>
            <div
              className={styles.monthlyPremium}
              data-testid={`monthly-price-block-${coverageProduct}`}>
              {`${formatCentsToEurosWithSeparators(premium.monthlyNetPremium.amountInCents)} ${t(`common.eurosPerMonth`)}`}
            </div>
            <div
              className={styles.annualPremium}
              data-testid={`annual-price-block-${coverageProduct}`}>
              {`${formatCentsToEurosWithSeparators(premium.annualNetPremium.amountInCents)} ${t('common.eurosPerY')}`}
            </div>
          </Col>
        )}
      </Row>
      {discountComponent && displayDetails && (
        <div
          className={styles.discount}
          data-testid={`discount-component-${coverageProduct}`}>
          {discountComponent}
        </div>
      )}
    </div>
  );
};

export default CoverageHeading;
